/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { LoggerService } from '@ao/core/services/logger.service';
import { appInsights } from '@ao/app/app.config';
import { ApiErrorResponse } from '@ao/shared/interfaces/api-error-response';

@Injectable()
export class ErrorService implements ErrorHandler {
  private notification = inject(LoggerService);
  private router = inject(Router);
  private lastNavigationStartAt = 0;
  private readonly NAVIGATION_ABORT_WINDOW_MS = 1500;

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.lastNavigationStartAt = Date.now();
      }
    });
  }

  handleError(error: Error | HttpErrorResponse): void {
    appInsights.trackException({ exception: error });

    if (error instanceof HttpErrorResponse) {
      if (this.isNavigationAbort(error)) {
        return;
      }
      if (error.status === 0) {
        this.notification.logApiError(
          'Unable to reach the server. Please check your connection and try again.'
        );
        return;
      }

      const parsedError = this.parseErrorObject(error.error);
      const consolidatedErrors: string[] = [];
      let showTraceId = false;

      switch (error.status) {
        case HttpStatusCode.BadRequest:
          if (parsedError.details) {
            if (
              typeof parsedError.details === 'object' &&
              !Array.isArray(parsedError.details)
            ) {
              Object.entries(parsedError.details).forEach(([key, messages]) => {
                consolidatedErrors.push(
                  `[${key}] ${Array.isArray(messages) ? messages.join(', ') : messages}`
                );
              });
            } else if (typeof parsedError.details === 'string') {
              consolidatedErrors.push(parsedError.details);
            }
          } else if (parsedError.message) {
            consolidatedErrors.push(parsedError.message);
          } else {
            consolidatedErrors.push('Bad request');
          }
          break;

        case HttpStatusCode.Unauthorized:
        case HttpStatusCode.Forbidden:
          consolidatedErrors.push(
            'You are not authorized to access this resource.'
          );
          if (parsedError?.message) {
            consolidatedErrors.push(parsedError?.message);
          }
          this.router.navigate(['/no-auth']);
          break;

        case HttpStatusCode.NotFound:
          if (parsedError.message) {
            consolidatedErrors.push(parsedError.message);
          } else {
            consolidatedErrors.push('Resource not found.');
          }
          break;

        default:
          if (parsedError.traceId) {
            showTraceId = false; // Set to true if you want to show trace ID in all errors
          }
          if (parsedError.message) {
            consolidatedErrors.push(parsedError.message);
          } else {
            consolidatedErrors.push('An unexpected error occurred.');
          }
          break;
      }

      consolidatedErrors.forEach(msg => {
        const fullMessage =
          showTraceId &&
          parsedError.traceId &&
          !msg.includes(parsedError.traceId)
            ? `${msg} (Trace ID: ${parsedError.traceId})`
            : msg;
        this.notification.logApiError(fullMessage);
      });

      if (
        error.status !== HttpStatusCode.Unauthorized &&
        error.status !== HttpStatusCode.Forbidden &&
        consolidatedErrors.length === 0
      ) {
        this.router.navigate(['/error']);
      }
    } else {
      if (
        !error.message.includes('Cannot read properties of undefined') &&
        !error.message.includes("can't access property") &&
        !error.message.includes('NG0100')
      ) {
        this.notification.logApiError(
          error.message ?? 'A client-side error occurred.'
        );
      }
    }
  }

  private parseErrorObject(error: ApiErrorResponse | string): {
    message?: string;
    traceId?: string;
    details?: Record<string, string[]> | string;
  } {
    try {
      let err: ApiErrorResponse | any;
      if (typeof error === 'string') {
        err = JSON.parse(error);
      } else {
        err = error;
      }
      return {
        message: err?.message || err?.title,
        traceId: err?.traceId,
        details: err?.error || err?.errors,
      };
    } catch {
      return { message: 'Unknown error' };
    }
  }

  private isNavigationAbort(error: HttpErrorResponse): boolean {
    if (error.status !== 0) {
      return false;
    }

    const inner: any = error.error;
    const name = (inner?.name || '').toLowerCase();
    if (name === 'aborterror') {
      return true;
    }

    const sinceNav = Date.now() - this.lastNavigationStartAt;
    return sinceNav >= 0 && sinceNav <= this.NAVIGATION_ABORT_WINDOW_MS;
  }
}
